/**
 * Permission checking hooks for component-level access control.
 * Includes useHasPermission for general checks and useIsAccountAdmin
 * for admin role verification. Supports wildcard matching.
 */
import { useAuthStore } from "@/stores/auth";

/**
 * Check if a permission set includes the required permission
 * Handles wildcards: "*" and "module.*"
 * @param permissions - Array of permission strings the user has
 * @param required - Permission string to check for
 */
function checkPermission(permissions: string[], required: string): boolean {
	// Full wildcard - has all permissions
	if (permissions.includes("*")) {
		return true;
	}

	// Exact match
	if (permissions.includes(required)) {
		return true;
	}

	// Module wildcard: "campaigns.*" matches "campaigns.edit"
	const [module] = required.split(".");
	if (module && permissions.includes(`${module}.*`)) {
		return true;
	}

	return false;
}

/**
 * Hook to check if the current user has a specific permission
 * @param permission - Permission string to check (e.g., "campaigns.edit")
 * @returns True if user has the permission
 */
export function usePermission(permission: string): boolean {
	const permissions = useAuthStore((state) => state.permissions);
	const user = useAuthStore((state) => state.user);

	// Super admins bypass all permission checks
	if (user?.role === "super_admin") {
		return true;
	}

	return checkPermission(permissions, permission);
}

/**
 * Hook to check if the current user has any of the specified permissions
 * @param requiredPermissions - Array of permission strings (user needs at least one)
 * @returns True if user has any of the permissions
 */
export function useAnyPermission(requiredPermissions: string[]): boolean {
	const permissions = useAuthStore((state) => state.permissions);
	const user = useAuthStore((state) => state.user);

	// Super admins bypass all permission checks
	if (user?.role === "super_admin") {
		return true;
	}

	return requiredPermissions.some((perm) => checkPermission(permissions, perm));
}

/**
 * Hook to check if the current user has all of the specified permissions
 * @param requiredPermissions - Array of permission strings (user needs all)
 * @returns True if user has all of the permissions
 */
export function useAllPermissions(requiredPermissions: string[]): boolean {
	const permissions = useAuthStore((state) => state.permissions);
	const user = useAuthStore((state) => state.user);

	// Super admins bypass all permission checks
	if (user?.role === "super_admin") {
		return true;
	}

	return requiredPermissions.every((perm) => checkPermission(permissions, perm));
}

/**
 * Hook to check if the current user is an account admin (has wildcard permission)
 * @returns True if user is an account admin or super admin
 */
export function useIsAccountAdmin(): boolean {
	const permissions = useAuthStore((state) => state.permissions);
	const user = useAuthStore((state) => state.user);

	// Super admins are always account admins
	if (user?.role === "super_admin") {
		return true;
	}

	return permissions.includes("*");
}

/**
 * Hook to get the current user's permission set
 * @returns Array of permission strings
 */
export function usePermissions(): string[] {
	return useAuthStore((state) => state.permissions);
}
